import React, { useState, useEffect } from 'react';
import { usersService, locationService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Search,
  Users,
  UserCheck,
  UserX,
  Edit,
  KeyRound,
  Shield,
  ShieldAlert,
  LockOpen,
  Loader2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const ROLES = [
  { value: 'employee', label: 'Employee', color: 'bg-gray-500/20 text-gray-400' },
  { value: 'manager', label: 'Manager', color: 'bg-green-500/20 text-green-400' },
  { value: 'store_manager', label: 'Store Manager', color: 'bg-teal-500/20 text-teal-400' },
  { value: 'hr', label: 'HR', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'admin', label: 'Admin', color: 'bg-red-500/20 text-red-400' },
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-500/20 text-purple-400' },
];

export default function UsersPage() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    location: '',
    allowed_locations: [],
  });

  const [showResetDialog, setShowResetDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(null);

  useEffect(() => {
    loadUsers();
    locationService.list().then(setLocations).catch(() => {});
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await usersService.list();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersService.updateRole(userId, newRole);
      toast.success('User role updated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await usersService.toggleStatus(userId);
      toast.success('User status updated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'employee',
      department: user.department || '',
      location: user.location || '',
      allowed_locations: user.allowed_locations || [],
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (editData.role === 'store_manager' && editData.allowed_locations.length === 0) {
      toast.error('Assign at least one location for a Store Manager');
      return;
    }
    setEditLoading(true);
    try {
      await usersService.update(selectedUser.id, editData);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleUnlock = async (userId) => {
    setUnlockLoading(userId);
    try {
      await usersService.unlockUser(userId);
      toast.success('Account unlocked');
      loadUsers();
    } catch (error) {
      toast.error('Failed to unlock account');
    } finally {
      setUnlockLoading(null);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password?')) return;

    setResetLoading(true);
    try {
      const data = await usersService.resetPassword(userId);
      setTempPassword(data.temporary_password);
      setShowResetDialog(true);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || ROLES[0];
    return (
      <Badge className={roleConfig.color}>
        {roleConfig.label}
      </Badge>
    );
  };

  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold tracking-tight">User Management</h1>
        <p className="text-muted-foreground mt-1">
          Manage user accounts and roles
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10 text-green-500">
                <UserCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">{users.filter(u => u.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-red-500/10 text-red-500">
                <UserX className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold">{users.filter(u => !u.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/10 text-purple-500">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold">
                  {users.filter(u => ['admin', 'super_admin'].includes(u.role)).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="user-search"
        />
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {user.username?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{user.username}</span>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Select
                        value={user.role}
                        onValueChange={(v) => handleRoleChange(user.id, v)}
                      >
                        <SelectTrigger className="w-32" data-testid={`role-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(role => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={user.is_active ? 'badge-success' : 'badge-danger'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {user.is_locked && (
                          <Badge className="bg-orange-500/20 text-orange-500 text-xs">Locked</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(user)}
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(user.id)}
                          title="Reset Password"
                          disabled={resetLoading}
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        {user.is_locked && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlock(user.id)}
                            title="Unlock Account"
                            disabled={unlockLoading === user.id}
                            className="text-orange-500"
                          >
                            {unlockLoading === user.id
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <LockOpen className="w-4 h-4" />}
                          </Button>
                        )}
                        <Switch
                          checked={user.is_active}
                          onCheckedChange={() => handleToggleStatus(user.id)}
                          data-testid={`toggle-status-${user.id}`}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Details</DialogTitle>
            <DialogDescription>
              Update account information for {selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={editData.role}
                  onValueChange={(v) => setEditData({ ...editData, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={editData.department}
                  onChange={(e) => setEditData({ ...editData, department: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={editData.location}
                  onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              />
            </div>
            {editData.role === 'store_manager' && (
              <div className="space-y-2">
                <Label>Allowed Locations (stores this manager can access)</Label>
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {locations.length === 0 && (
                    <p className="text-sm text-muted-foreground">No locations available</p>
                  )}
                  {locations.map(loc => (
                    <label key={loc.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={editData.allowed_locations.includes(loc.id)}
                        onCheckedChange={(checked) => {
                          const next = checked
                            ? [...editData.allowed_locations, loc.id]
                            : editData.allowed_locations.filter(id => id !== loc.id);
                          setEditData({ ...editData, allowed_locations: next });
                        }}
                      />
                      {loc.name}
                    </label>
                  ))}
                </div>
                {editData.allowed_locations.length === 0 && (
                  <p className="text-xs text-destructive">Assign at least one location for a Store Manager</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={editLoading}>
              {editLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-warning" />
              Password Reset
            </DialogTitle>
            <DialogDescription>
              A temporary password has been generated. Please securely share this with the user.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Temporary Password</p>
            <div className="p-4 bg-muted rounded-lg border-2 border-dashed border-primary/20">
              <code className="text-2xl font-bold tracking-wider text-primary select-all">
                {tempPassword}
              </code>
            </div>
            <p className="text-xs text-muted-foreground mt-4 italic">
              User will be forced to change this on their next login.
            </p>
          </div>
          <DialogFooter>
            <Button className="w-full" onClick={() => setShowResetDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
